import {
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { DeleteIcon } from '@atgfr8/icons';
import { ToastType } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import {
  carrierDetailsStore,
  updateCarrierComment,
  updateCarrierDetailsState,
} from '@store/carriers';
import {
  AddOrEditCommentPayload,
  applyCustomerDetailsHydration,
  customerDetailsStore,
  updateCustomerComment,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { openDialogBox } from '@store/dialogBox';
import {
  updateVendorComment,
  updateVendorDetailsState,
  vendorDetailsStore,
} from '@store/vendors/vendorDetails';
import { Comments } from '@typeDefinitions/comments';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { handleToast, isAdmin, isSuperAdmin } from '@utils/utils';
import {
  canDeleteCarrierComment,
  isAdminOrOriginalAuthor,
} from '@views/carriers/carrierDetails/carrierDetailsTab/utils';
import { DELETE_COMMENT_DIALOG_ID } from '@views/order/constants';
import { DateTime } from 'luxon';
import { Component, createSignal, onMount, Setter, Show } from 'solid-js';

type SingleCommentCardProps = {
  comment: Comments;
  type:
    | 'Carrier'
    | 'Order'
    | 'Customer'
    | 'FactoringCompany'
    | 'Claim'
    | 'Call Log'
    | 'Prospect'
    | 'CarrierInvoicing'
    | 'CustomerInvoicing'
    | 'VendorDetails';
  setCommentIdToDelete: Setter<number | null>;
  changeTrackingSource?: ChangeTrackingSource;
};

const textStyle = {
  color: 'black',
  fontSize: '16px',
  fontWeight: '400',
  flex: '1',
};
const titleStyle = { ...textStyle, fontWeight: '500' };

export const SingleCommentCard: Component<SingleCommentCardProps> = (props) => {
  const [editable, setEditable] = createSignal(false);
  const [comment, setComment] = createSignal('');
  const [loading, setLoading] = createSignal(false);

  onMount(() => {
    setComment(props.comment.comment ?? '');
  });

  const canEditComment = () => {
    switch (props.type) {
      case 'Customer':
      case 'Carrier':
      case 'VendorDetails':
        return isAdminOrOriginalAuthor(props.comment);
      default:
        return false;
    }
  };

  const canDelete = (comment: Comments) => {
    switch (props.type) {
      case 'Customer':
      case 'VendorDetails':
        return isAdminOrOriginalAuthor(comment);
      case 'Carrier':
        return canDeleteCarrierComment(comment) && comment.canDelete;
      case 'Claim':
        return isSuperAdmin();
      case 'FactoringCompany':
        return isSuperAdmin();
      default:
        return isAdmin();
    }
  };

  const handleEditComment = () => {
    if (canEditComment()) setEditable(true);
  };

  const handleCommentSubmission = async (e: KeyboardEvent) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    setEditable(false);
    setLoading(true);
    const payload = {
      id: props.comment.id,
      comment: comment(),
      type: props.comment.type,
    } as AddOrEditCommentPayload;

    const res =
      props.type === 'Customer'
        ? await updateCustomerComment(payload)
        : props.type === 'VendorDetails'
          ? await updateVendorComment(payload, props.changeTrackingSource)
          : await updateCarrierComment(payload, props.changeTrackingSource);

    if (Boolean(res)) {
      if (props.type === 'Carrier') {
        const carrierDetails = carrierDetailsStore.carrierDetails;
        updateCarrierDetailsState({
          comments: carrierDetails?.comments.map((c) =>
            c.id === props.comment.id ? { ...c, comment: comment() } : c,
          ),
        });
      } else if (props.type === 'Customer') {
        const customerDetails = customerDetailsStore.customer;
        applyCustomerDetailsHydration(() => {
          updateCustomerDetailsState({
            comments: customerDetails.comments.map((c) =>
              c.id === props.comment.id ? { ...c, comment: comment() } : c,
            ),
          });
        });
      } else if (props.type === 'VendorDetails') {
        const vendorDetails = vendorDetailsStore.vendorDetails;
        updateVendorDetailsState({
          comments: vendorDetails?.comments.map((c) =>
            c.id === props.comment.id ? { ...c, comment: comment() } : c,
          ),
        });
      }

      handleToast(ToastType.Success, 'Comment updated successfully');
    } else {
      setComment(props.comment.comment ?? '');
    }
    setLoading(false);
  };

  return (
    <Stack spacing={1}>
      <Show when={loading()}>
        <AtgLoader />
      </Show>
      <Grid item container class="bg-[#EEE] p-1 h-[48px]">
        <Typography
          class="flex items-center h-full p-[5px]"
          variant="subtitle2"
          sxProps={titleStyle}
        >
          {props.comment.name ?? '-'}
          <Show when={props.type === 'Carrier' && !!props.comment.type}>
            <Chip
              text={props.comment.type}
              color="info"
              variant="filled"
              id={`chip-${props.comment.id}`}
              class="ml-2"
            />
          </Show>
        </Typography>
        <Grid
          size={6}
          container
          gap={2}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Typography
            variant="body2"
            component="span"
            sxProps={{ ...textStyle, textAlign: 'end' }}
          >
            {DateTime.fromISO(props.comment.timestamp as string, {
              zone: 'utc',
            })
              .setZone('local')
              .toFormat('MMM dd, yyyy hh:mm a')
              .toUpperCase()}
          </Typography>
          {canDelete(props.comment) && (
            <DeleteIcon
              color="#B00020"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                openDialogBox(`${DELETE_COMMENT_DIALOG_ID}-${props.type}`);
                props.setCommentIdToDelete(props.comment.id);
              }}
            />
          )}
        </Grid>
      </Grid>
      <Grid spacing={2} pb={3} class="flex!">
        <Show
          when={editable()}
          fallback={
            <span
              class={`${
                props.type === 'Customer' ||
                props.type === 'Carrier' ||
                props.type === 'VendorDetails'
                  ? 'cursor-pointer'
                  : ''
              }`}
              onClick={handleEditComment}
            >
              {comment()
                .split('\n')
                .map((line) => (
                  <span class="break-all">
                    {line}
                    <br />
                  </span>
                ))}
            </span>
          }
        >
          <TextField
            multiline
            rows={3}
            name="comment"
            label=""
            value={comment()}
            onChange={(value: string) => {
              setComment(value);
            }}
            onKeyDown={handleCommentSubmission}
            class="ml-2! resize-y"
            dataTestId={'comment-textInput'}
          />
        </Show>
      </Grid>
    </Stack>
  );
};
